import { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { suggestSupportDeskMessage } from '../../services/supportDeskApi';
import { SupportAiSuggestButton } from './SupportAiSuggestButton';

type SupportArchivePanelProps = {
  mode: 'archive' | 'reopen';
  ticketId?: string;
  ticketCode?: string | null;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void> | void;
};

export function SupportArchivePanel({
  mode,
  ticketId,
  ticketCode,
  submitting = false,
  onClose,
  onSubmit,
}: SupportArchivePanelProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  const isArchive = mode === 'archive';
  const title = isArchive ? 'Arquivar atendimento' : 'Reabrir atendimento';
  const submitLabel = isArchive ? 'Arquivar' : 'Reabrir';
  const placeholder = isArchive
    ? 'Ex.: Cliente confirmou a solução; acesso restaurado; duplicata do Ticket#…'
    : 'Opcional — por que o chamado voltou à fila?';
  const hint = isArchive
    ? 'O chamado sai das filas ativas e o motivo fica como nota interna.'
    : 'O chamado volta às filas ativas com status Reaberto.';

  async function handleSuggestWithAi(): Promise<void> {
    if (!ticketId || suggesting || submitting) return;
    setSuggesting(true);
    setError(null);
    try {
      const result = await suggestSupportDeskMessage(
        ticketId,
        isArchive ? 'ArchiveReason' : 'ReopenReason',
        reason,
      );
      setReason(result.text);
      toast.success(
        isArchive
          ? 'Sugestão de motivo gerada. Revise antes de arquivar.'
          : 'Sugestão de motivo gerada. Revise antes de reabrir.',
      );
    } catch (err) {
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      const message = detail ?? 'Não foi possível sugerir o motivo com IA.';
      setError(message);
      toast.error(message);
    } finally {
      setSuggesting(false);
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const trimmed = reason.trim();
    if (isArchive && !trimmed) {
      setError('Informe o motivo do arquivamento.');
      return;
    }
    if (trimmed.length > 2000) {
      setError('O motivo deve ter no máximo 2000 caracteres.');
      return;
    }
    setError(null);
    await onSubmit(trimmed);
  }

  return (
    <form
      onSubmit={(event) => void handleSubmit(event)}
      className="flex h-full min-h-0 flex-col border-t border-secondary-200 bg-secondary-50"
    >
      <div className="flex items-start justify-between gap-3 border-b border-secondary-200 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-secondary-900">{title}</h3>
          <p className="mt-0.5 text-xs text-secondary-500">
            {ticketCode ? `${ticketCode} · ` : ''}
            {hint}
          </p>
        </div>
        <button
          type="button"
          className="text-xs font-medium text-secondary-500 hover:text-secondary-800"
          onClick={onClose}
          disabled={submitting || suggesting}
        >
          Cancelar
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <label className="block text-xs font-medium text-secondary-700" htmlFor="desk-archive-reason">
            {isArchive ? 'Motivo (obrigatório)' : 'Motivo (opcional)'}
          </label>
          {ticketId ? (
            <SupportAiSuggestButton
              suggesting={suggesting}
              disabled={submitting}
              onClick={() => void handleSuggestWithAi()}
              title={
                isArchive
                  ? 'Sugerir motivo de arquivamento com IA'
                  : 'Sugerir motivo de reabertura com IA'
              }
            />
          ) : null}
        </div>
        <textarea
          id="desk-archive-reason"
          className="erp-input min-h-[7rem] w-full resize-y text-sm"
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            if (error) setError(null);
          }}
          placeholder={placeholder}
          disabled={submitting || suggesting}
          autoFocus
          maxLength={2000}
        />
        <div className="flex items-center justify-between gap-2 text-xs text-secondary-400">
          <span>
            {error ? <span className="text-red-600">{error}</span> : null}
            {!error ? (
              <span>A IA usa o histórico do chamado e o que você já escreveu.</span>
            ) : null}
          </span>
          <span>{reason.trim().length}/2000</span>
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-secondary-200 px-4 py-3">
        <button
          type="button"
          className="erp-btn-secondary text-sm"
          onClick={onClose}
          disabled={submitting || suggesting}
        >
          Voltar
        </button>
        <button
          type="submit"
          className="erp-btn-primary text-sm"
          disabled={submitting || suggesting || (isArchive && !reason.trim())}
        >
          {submitting ? 'Salvando…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
